import { addYears } from 'date-fns';
import { serviceModel } from "../../models";
import { DiscountPeriodExpirationModel } from "../../models/DiscountPeriodExpirationModel";
import { DiscountExpirationType, LimitedTimeDiscountOfferModel } from "../../models/LimitedTimeDiscountOfferModel";
import { PriceIncreaseModel } from "../../models/PriceIncreaseModel";
import { DEFAULT_DISCOUNT_DEADLINE_DAYS, DEFAULT_DISCOUNT_EXPIRATION_YEARS, DEFAULT_LIMIT_BILLING_TIMES, DEFAULT_OPTIONAL_INCLUDED_BY_DEFAULT, DEFAULT_PRACTICE_MANAGEMENT_ENABLED, DEFAULT_PRACTICE_MANAGEMENT_PRESERVE_EXISTING, DEFAULT_PRACTICE_MANAGEMENT_TEAM_MEMBERS, DEFAULT_PRICE_INCREASE_DATE, DEFAULT_PRICE_INCREASE_PERCENT, DEFAULT_PRICE_INCREASE_ROUND_PRICES, DEFAULT_QBO_CLASS_ID } from "./AdvancedSettingDefaults";
import { AdvancedSetting, IncreaseMethod } from "./ServiceAdvancedSettings.types";
import { getInitialIncreaseMethod, getInitialStartBillingAtDate, getValidIncreaseOn } from "./ServiceAdvancedSettings.utils";
/**
 * Enables an advanced setting with its default values by calling the appropriate handler.
 */
export function enableAdvancedSetting(type, ctx, handlers) {
  var _handlers$onPausedCha, _handlers$onBillOnAcc, _handlers$onProratedC, _handlers$onOptionalC, _handlers$onLimitBill, _handlers$onQboClassC, _handlers$onFinancial, _handlers$onKarbonCha, _handlers$onLimitedTi, _handlers$onDiscountP, _handlers$onPreapprov;
  const serviceId = ctx.service.id;
  switch (type) {
    case AdvancedSetting.Pause:
      (_handlers$onPausedCha = handlers.onPausedChange) === null || _handlers$onPausedCha === void 0 || _handlers$onPausedCha.call(handlers, serviceId, true);
      return;
    case AdvancedSetting.BillOnApproval:
      (_handlers$onBillOnAcc = handlers.onBillOnAcceptanceChange) === null || _handlers$onBillOnAcc === void 0 || _handlers$onBillOnAcc.call(handlers, serviceId, true);
      return;
    case AdvancedSetting.Prorate:
      (_handlers$onProratedC = handlers.onProratedChange) === null || _handlers$onProratedC === void 0 || _handlers$onProratedC.call(handlers, serviceId, true);
      return;
    case AdvancedSetting.Optional:
      (_handlers$onOptionalC = handlers.onOptionalChange) === null || _handlers$onOptionalC === void 0 || _handlers$onOptionalC.call(handlers, serviceId, true, DEFAULT_OPTIONAL_INCLUDED_BY_DEFAULT);
      return;
    case AdvancedSetting.LimitCharges:
      (_handlers$onLimitBill = handlers.onLimitBillingTimesChange) === null || _handlers$onLimitBill === void 0 || _handlers$onLimitBill.call(handlers, serviceId, true, ctx.SERVICE_SETTINGS_DROPDOWN ? DEFAULT_LIMIT_BILLING_TIMES : null);
      return;
    case AdvancedSetting.AutoPriceIncrease:
      {
        var _handlers$onAutoPrice;
        const increaseOn = getValidIncreaseOn(DEFAULT_PRICE_INCREASE_DATE(ctx.now), ctx.now);
        const increaseMethod = getInitialIncreaseMethod(PriceIncreaseModel.makeNone(), ctx.relationshipState);
        const priceIncrease = PriceIncreaseModel.make(DEFAULT_PRICE_INCREASE_ROUND_PRICES, DEFAULT_PRICE_INCREASE_PERCENT, increaseMethod === IncreaseMethod.Date ? increaseOn : null);
        (_handlers$onAutoPrice = handlers.onAutoPriceIncreaseChange) === null || _handlers$onAutoPrice === void 0 || _handlers$onAutoPrice.call(handlers, serviceId, priceIncrease);
        return;
      }
    case AdvancedSetting.QboClasses:
      (_handlers$onQboClassC = handlers.onQboClassChange) === null || _handlers$onQboClassC === void 0 || _handlers$onQboClassC.call(handlers, serviceId, serviceModel.makeIntegratedClassSet(DEFAULT_QBO_CLASS_ID));
      return;
    case AdvancedSetting.FinancialCents:
      (_handlers$onFinancial = handlers.onFinancialCentsChange) === null || _handlers$onFinancial === void 0 || _handlers$onFinancial.call(handlers, serviceId, {
        teamMembers: DEFAULT_PRACTICE_MANAGEMENT_TEAM_MEMBERS,
        enabled: DEFAULT_PRACTICE_MANAGEMENT_ENABLED
      }, {
        preserveExistingTeamMembers: DEFAULT_PRACTICE_MANAGEMENT_PRESERVE_EXISTING
      });
      return;
    case AdvancedSetting.Karbon:
      (_handlers$onKarbonCha = handlers.onKarbonChange) === null || _handlers$onKarbonCha === void 0 || _handlers$onKarbonCha.call(handlers, serviceId, {
        teamMembers: DEFAULT_PRACTICE_MANAGEMENT_TEAM_MEMBERS,
        enabled: DEFAULT_PRACTICE_MANAGEMENT_ENABLED
      }, {
        preserveExistingTeamMembers: DEFAULT_PRACTICE_MANAGEMENT_PRESERVE_EXISTING
      });
      return;
    case AdvancedSetting.LimitedTimeDiscountOffer:
      (_handlers$onLimitedTi = handlers.onLimitedTimeDiscountOfferChange) === null || _handlers$onLimitedTi === void 0 || _handlers$onLimitedTi.call(handlers, serviceId, new LimitedTimeDiscountOfferModel({
        type: DiscountExpirationType.Days,
        days: DEFAULT_DISCOUNT_DEADLINE_DAYS
      }));
      return;
    case AdvancedSetting.LimitedTimeDiscountOfferForAgreement:
      (_handlers$onDiscountP = handlers.onDiscountPeriodExpirationChange) === null || _handlers$onDiscountP === void 0 || _handlers$onDiscountP.call(handlers, serviceId, ctx.relationshipState.type === 'approved' ? DiscountPeriodExpirationModel.makeDate(addYears(ctx.now, 1)) : DiscountPeriodExpirationModel.makePeriod('years', DEFAULT_DISCOUNT_EXPIRATION_YEARS));
      return;
    case AdvancedSetting.PreapprovedAmount:
      (_handlers$onPreapprov = handlers.onPreapprovedAmountChange) === null || _handlers$onPreapprov === void 0 || _handlers$onPreapprov.call(handlers, serviceId, null, true);
      return;
    case AdvancedSetting.StartAutomaticBillingAt:
      {
        var _handlers$onStartBill;
        const startBillingAt = getInitialStartBillingAtDate({
          now: ctx.now,
          relationshipEffectiveDate: ctx.relationshipState.effectiveDate,
          isAmendedService: serviceModel.isFromAmendment(ctx.service)
        });
        (_handlers$onStartBill = handlers.onStartBillingAtChange) === null || _handlers$onStartBill === void 0 || _handlers$onStartBill.call(handlers, serviceId, startBillingAt);
        return;
      }
  }
}